import { useCallback, useEffect, useState } from 'react';
import { applyZohoVisitorIdentity } from './zohoSalesIQ.visitor';

const SALESIQ_WIDGET_CODE = import.meta.env.VITE_ZOHO_SALESIQ_ID!;
const SALESIQ_SCRIPT_URL =
  import.meta.env.VITE_SALESIQ_SCRIPT_URL ||
  `https://salesiq.zohopublic.com/widget?wc=${encodeURIComponent(SALESIQ_WIDGET_CODE)}`;
const SALESIQ_SCRIPT_ID = 'zsiqscript';
const READY_TIMEOUT_MS = 12_000;

declare global {
  interface Window {
    $zoho: any;
  }
}

/** Widget APIs are global — one load lifecycle for the whole app. */
let globalLoaded = false;
let loadPromise: Promise<void> | null = null;
const loadedListeners = new Set<() => void>();

const getZI = () => window?.$zoho?.salesiq;

/** True when the SalesIQ script has initialized widget APIs. */
const isWidgetReady = (): boolean => {
  const zi = getZI();
  return !!(zi?.floatwindow || zi?.floatbutton);
};

const notifyLoaded = () => {
  if (globalLoaded) return;
  globalLoaded = true;
  loadedListeners.forEach((listener) => listener());
};

const applyReadySideEffects = () => {
  try {
    window.$zoho.salesiq.floatbutton?.position?.('right');
    window.$zoho.salesiq.floatbutton?.visible?.('hide');
    window.$zoho.salesiq.floatbutton?.hide?.();
  } catch {
    // ignore
  }
};

const syncVisitorIdentity = () => {
  const zi = getZI();
  if (zi) applyZohoVisitorIdentity(zi);
};

const cleanupOnError = (err: unknown, reject: (reason: Error) => void) => {
  const tag = document.querySelector<HTMLScriptElement>(`#${SALESIQ_SCRIPT_ID}`);
  if (tag) tag.parentElement?.removeChild(tag);

  try {
    if (window.$zoho?.salesiq) {
      delete window.$zoho.salesiq.ready;
    }
  } catch {
    // ignore
  }

  loadPromise = null;
  globalLoaded = false;
  reject(err instanceof Error ? err : new Error(String(err)));
};

const startLoad = (): Promise<void> => {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    window.$zoho = window.$zoho || {};
    window.$zoho.salesiq = window.$zoho.salesiq || {
      ready: function () {},
    };

    const readyTimeout = setTimeout(() => {
      cleanupOnError(new Error('Zoho SalesIQ widget did not become ready (timeout).'), reject);
    }, READY_TIMEOUT_MS);

    const onReady = () => {
      clearTimeout(readyTimeout);
      applyReadySideEffects();
      syncVisitorIdentity();
      notifyLoaded();
      resolve();
    };

    // Widget already initialized (e.g. user navigated to another route using a new hook instance).
    if (isWidgetReady()) {
      onReady();
      return;
    }

    const previousReady = window.$zoho.salesiq.ready;
    window.$zoho.salesiq.ready = () => {
      if (typeof previousReady === 'function') {
        try {
          previousReady();
        } catch {
          // ignore
        }
      }
      onReady();
    };

    let script = document.querySelector<HTMLScriptElement>(`#${SALESIQ_SCRIPT_ID}`);
    if (!script) {
      script = document.createElement('script');
      script.id = SALESIQ_SCRIPT_ID;
      script.src = SALESIQ_SCRIPT_URL;
      script.defer = true;

      script.onerror = () => {
        cleanupOnError(new Error('Failed to load Zoho SalesIQ script.'), reject);
      };

      document.head.appendChild(script);
    } else {
      script.onerror = () => {
        cleanupOnError(new Error('Zoho SalesIQ script error (existing tag).'), reject);
      };
    }
  });

  return loadPromise;
};

const ensureLoaded = async (): Promise<void> => {
  if (globalLoaded || isWidgetReady()) {
    if (!globalLoaded) {
      applyReadySideEffects();
      syncVisitorIdentity();
      notifyLoaded();
    }
    return;
  }

  await startLoad();
};

export function useZohoSalesIQ() {
  const [isLoaded, setIsLoaded] = useState(() => globalLoaded || isWidgetReady());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const syncLoaded = () => setIsLoaded(true);
    if (globalLoaded || isWidgetReady()) {
      setIsLoaded(true);
    }
    loadedListeners.add(syncLoaded);
    return () => {
      loadedListeners.delete(syncLoaded);
    };
  }, []);

  const openChat = useCallback(async () => {
    await ensureLoaded();
    syncVisitorIdentity();
    const zi = getZI();
    zi?.floatwindow?.visible?.('show');
    zi?.floatwindow?.open?.();
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(async () => {
    await ensureLoaded();
    const zi = getZI();
    zi?.floatwindow?.minimize?.();
    setIsOpen(false);
  }, []);

  const toggleChat = useCallback(async () => {
    const ready = globalLoaded || isWidgetReady();
    if (!ready) {
      await openChat();
      return;
    }
    if (isOpen) await closeChat();
    else await openChat();
  }, [isOpen, openChat, closeChat]);

  return {
    loadZohoSalesIQ: openChat,
    toggleChat,
    openChat,
    closeChat,
    isLoaded,
    isOpen,
  };
}
